import { useCallback, useEffect, useMemo, useState } from "react";
import { Menu } from "lucide-react";
import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";

const EMPTY_FORM = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  tax_number: "",
  notes: "",
  is_active: true,
};

const ITEMS_PER_PAGE = 10;

export default function AdminSuppliers() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [summaryView, setSummaryView] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [editingSupplier, setEditingSupplier] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [purchaseSupplier, setPurchaseSupplier] = useState(null);
  const [paymentSupplier, setPaymentSupplier] = useState(null);
  const [productsSupplier, setProductsSupplier] = useState(null);
  const [detailsSupplier, setDetailsSupplier] = useState(null);


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const token = localStorage.getItem("authToken");

  const getErrorMessage = async (response) => {
    try {
      const data = await response.json();
      return data.message || "Ndodhi një gabim.";
    } catch {
      return "Ndodhi një gabim gjatë komunikimit me serverin.";
    }
  };

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_BASE_URL}/api/suppliers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      const data = await response.json();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error("Gabim gjatë marrjes së furnitorëve:", requestError);
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) return;

        const data = await response.json();
        const productList = Array.isArray(data)
          ? data
          : Array.isArray(data.products)
            ? data.products
            : [];

        if (isMounted) setProducts(productList);
      } catch (requestError) {
        console.error("Gabim gjatë marrjes së produkteve:", requestError);
      }
    };

    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, [token, successMessage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, summaryView]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage("");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const statistics = useMemo(() => {
    const totalPurchases = suppliers.reduce(
      (total, supplier) => total + Number(supplier.total_purchases || 0),
      0,
    );
    const totalPaid = suppliers.reduce(
      (total, supplier) => total + Number(supplier.total_paid || 0),
      0,
    );
    const totalDue = suppliers.reduce(
      (total, supplier) => total + Number(supplier.total_due || 0),
      0,
    );

    return {
      total: suppliers.length,
      totalPurchases,
      totalPaid,
      totalDue,
    };
  }, [suppliers]);

  const productsBySupplier = useMemo(() => {
    return products.reduce((groups, product) => {
      if (product.supplier_id === null || product.supplier_id === undefined) {
        return groups;
      }

      const supplierId = String(product.supplier_id);
      if (!groups[supplierId]) groups[supplierId] = [];
      groups[supplierId].push(product);
      return groups;
    }, {});
  }, [products]);

  const filteredSuppliers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return suppliers.filter((supplier) => {
      const supplierStatus =
        Number(supplier.is_active) === 1 ? "active" : "inactive";

      const matchesStatus =
        statusFilter === "all" || supplierStatus === statusFilter;

      const matchesSummary =
        summaryView === "all" ||
        (summaryView === "paid" &&
          Number(supplier.total_purchases || 0) > 0 &&
          Number(supplier.total_due || 0) <= 0) ||
        (summaryView === "due" && Number(supplier.total_due || 0) > 0);

      const searchableText = [
        supplier.name,
        supplier.contact_person,
        supplier.phone,
        supplier.email,
        supplier.address,
        supplier.tax_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesStatus && matchesSummary && matchesSearch;
    });
  }, [suppliers, searchTerm, statusFilter, summaryView]);

  const showSupplierList = (view) => {
    setSummaryView(view);
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);

    window.setTimeout(() => {
      document
        .getElementById("suppliers-list")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const summaryViewTitle = {
    all: "Lista e të gjithë furnitorëve",
    paid: "Furnitorët me pagesa të regjistruara",
    due: "Furnitorët pa shlyer ose pjesërisht të shlyer",
  }[summaryView];

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSuppliers.length / ITEMS_PER_PAGE),
  );

  const paginatedSuppliers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSuppliers.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE,
    );
  }, [filteredSuppliers, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openCreateModal = () => {
    setEditingSupplier(null);
    setFormData(EMPTY_FORM);
    setError("");
    setIsModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);

    setFormData({
      name: supplier.name || "",
      contact_person: supplier.contact_person || "",
      phone: supplier.phone || "",
      email: supplier.email || "",
      address: supplier.address || "",
      tax_number: supplier.tax_number || "",
      notes: supplier.notes || "",
      is_active: Number(supplier.is_active) === 1,
    });

    setError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSupplier(null);
    setFormData(EMPTY_FORM);
    setError("");
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((currentForm) => ({
      ...currentForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      setError("Emri i furnitorit është i detyrueshëm.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const isEditing = Boolean(editingSupplier);

      const endpoint = isEditing
        ? `${API_BASE_URL}/api/suppliers/${editingSupplier.id}`
        : `${API_BASE_URL}/api/suppliers`;

      const response = await fetch(endpoint, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          name: formData.name.trim(),
          contact_person: formData.contact_person.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          address: formData.address.trim(),
          tax_number: formData.tax_number.trim(),
          notes: formData.notes.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      const result = await response.json();

      setSuccessMessage(
        result.message ||
          (isEditing
            ? "Furnitori u përditësua me sukses."
            : "Furnitori u shtua me sukses."),
      );

      closeModal();
      await fetchSuppliers();
    } catch (requestError) {
      console.error("Gabim gjatë ruajtjes së furnitorit:", requestError);
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (supplier) => {
    const newStatus = Number(supplier.is_active) !== 1;

    try {
      setError("");

      const response = await fetch(
        `${API_BASE_URL}/api/suppliers/${supplier.id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: newStatus,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      const result = await response.json();

      setSuppliers((currentSuppliers) =>
        currentSuppliers.map((currentSupplier) =>
          currentSupplier.id === supplier.id
            ? {
                ...currentSupplier,
                is_active: result.is_active,
              }
            : currentSupplier,
        ),
      );

      setSuccessMessage(result.message);
    } catch (requestError) {
      console.error("Gabim gjatë ndryshimit të statusit:", requestError);
      setError(requestError.message);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-800">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* HAMBURGER - VETËM MOBILE */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="fixed left-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white shadow-lg transition-all duration-200 hover:bg-blue-800 active:scale-95 lg:hidden"
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>

      <main className="min-h-screen min-w-0 overflow-x-hidden px-4 pb-8 pt-20 sm:px-6 lg:ml-64 lg:w-[calc(100%-16rem)] lg:overflow-visible lg:p-8">
      <div className="mx-auto max-w-[1600px]">
        {/* Header */}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Informacion mbi furnitorët
            </span>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
              Furnitorët
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Menaxho furnitorët dhe produktet e lidhura me ta.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
          

            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              <span className="text-xl leading-none">+</span>
              Shto furnitor
            </button>
          </div>
        </div>

        {/* Mesazhet */}

        {successMessage && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
            {successMessage}
          </div>
        )}

        {error && !isModalOpen && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        {/* Statistikat */}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Furnitorë gjithsej"
            value={statistics.total}
            color="blue"
            hint="Kliko për listën"
            active={summaryView === "all"}
            onClick={() => showSupplierList("all")}
          />

          <StatCard
            title="Blerje totale"
            value={formatMoney(statistics.totalPurchases)}
            color="purple"
            hint="Shko te produktet"
            onClick={() => {
              window.location.href = "/admin/products";
            }}
          />

          <StatCard
            title="Shlyer"
            value={formatMoney(statistics.totalPaid)}
            color="green"
            hint="Shiko pagesat dhe furnitorët"
            active={summaryView === "paid"}
            onClick={() => showSupplierList("paid")}
          />

          <StatCard
            title="Detyrim i mbetur"
            value={formatMoney(statistics.totalDue)}
            color="red"
            hint="Shiko furnitorët pa shlyer"
            active={summaryView === "due"}
            onClick={() => showSupplierList("due")}
          />
        </div>

        {/* Filtrat */}

        <div
          id="suppliers-list"
          className="mb-5 scroll-mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {summaryViewTitle}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {summaryView === "paid"
                  ? "Shfaqen furnitorët që kanë të paktën një shumë të paguar."
                  : summaryView === "due"
                    ? "Shfaqen furnitorët që kanë ende detyrim për t’u paguar."
                    : "Shfaqen të gjithë furnitorët e regjistruar."}
              </p>
            </div>
            <span className="inline-flex w-fit rounded-full bg-blue-50 px-3 py-1.5 text-sm font-bold text-blue-700">
              {filteredSuppliers.length} furnitorë
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px]">
            <div className="relative">
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Kërko sipas emrit, telefonit, email-it ose NIPT-it..."
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pl-11 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />

              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z"
                />
              </svg>
            </div>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            >
              <option value="all">Të gjithë furnitorët</option>
              <option value="active">Vetëm aktivë</option>
              <option value="inactive">Vetëm joaktivë</option>
            </select>
          </div>
        </div>

        {/* Tabela */}

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Kartat për mobile */}

          <div className="space-y-4 bg-slate-50 p-3 md:hidden">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                Duke ngarkuar furnitorët...
              </div>
            ) : paginatedSuppliers.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
                <p className="font-semibold text-slate-700">
                  Nuk u gjet asnjë furnitor
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Ndrysho filtrat ose shto furnitorin e parë.
                </p>
              </div>
            ) : (
              paginatedSuppliers.map((supplier) => (
                <article
                  key={supplier.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-bold text-slate-900">
                        {supplier.name}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        NIPT: {supplier.tax_number || "—"}
                      </p>
                    </div>
                    <PaymentStatusBadge status={supplier.payment_status} />
                  </div>

                  <div className="space-y-4 p-4">
                    <div className="grid grid-cols-1 gap-2 rounded-xl bg-slate-50 p-3 text-sm">
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Telefoni</span>
                        <span className="font-semibold text-slate-700">
                          {supplier.phone || "—"}
                        </span>
                      </p>
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Email</span>
                        <span className="max-w-[65%] truncate font-semibold text-slate-700">
                          {supplier.email || "—"}
                        </span>
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <MobileAmountCard
                        label="Blerje"
                        value={supplier.total_purchases}
                        color="purple"
                      />
                      <MobileAmountCard
                        label="Shlyer"
                        value={supplier.total_paid}
                        color="green"
                      />
                      <MobileAmountCard
                        label="Detyrim"
                        value={supplier.total_due}
                        color="red"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setDetailsSupplier(supplier)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-violet-100 bg-violet-50 p-3 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-violet-700">
                          {productsBySupplier[String(supplier.id)]?.length ||
                            Number(supplier.products_count || 0)}{" "}
                          produkte
                        </span>
                        <span className="mt-1 block truncate text-xs text-violet-600">
                          {productsBySupplier[String(supplier.id)]?.length
                            ? productsBySupplier[String(supplier.id)]
                                .slice(0, 2)
                                .map((product) => product.name)
                                .join(", ")
                            : "Shiko produktet e furnitorit"}
                        </span>
                      </span>
                      <span className="shrink-0 font-bold text-violet-700">→</span>
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setDetailsSupplier(supplier)}
                        className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-xs font-bold text-slate-700"
                      >
                        Detaje
                      </button>
                      <button
                        type="button"
                        onClick={() => setPurchaseSupplier(supplier)}
                        className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs font-bold text-violet-700"
                      >
                        + Blerje
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentSupplier(supplier)}
                        disabled={Number(supplier.total_due || 0) <= 0}
                        className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition ${
                          Number(supplier.total_due || 0) > 0
                            ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700"
                        } disabled:cursor-default`}
                      >
                        {Number(supplier.total_due || 0) > 0
                          ? "+ Regjistro pagesë"
                          : "✓ Shlyer"}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditModal(supplier)}
                        className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs font-bold text-blue-700"
                      >
                        Modifiko
                      </button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          {/* Tabela për tablet dhe desktop */}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <TableHeader>Furnitori</TableHeader>
                  <TableHeader>Kontakti</TableHeader>
                  <TableHeader>Blerje totale</TableHeader>
                  <TableHeader>Shlyer</TableHeader>
                  <TableHeader>Detyrim</TableHeader>
                  <TableHeader>Produkte</TableHeader>
                  <TableHeader>Statusi</TableHeader>
                  <TableHeader align="right">Veprime</TableHeader>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-16 text-center text-sm text-slate-500"
                    >
                      Duke ngarkuar furnitorët...
                    </td>
                  </tr>
                ) : paginatedSuppliers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-16 text-center"
                    >
                      <p className="font-semibold text-slate-700">
                        Nuk u gjet asnjë furnitor
                      </p>

                      <p className="mt-1 text-sm text-slate-500">
                        Ndrysho filtrat ose shto furnitorin e parë.
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedSuppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className="transition hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {supplier.name}
                          </p>

                          <p className="mt-1 text-xs text-slate-500">
                            NIPT: {supplier.tax_number || "—"}
                          </p>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="space-y-1 text-sm">
                          <p className="text-slate-700">
                            {supplier.phone || "—"}
                          </p>

                          <p className="text-xs text-slate-500">
                            {supplier.email || "—"}
                          </p>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-slate-800">
                        {formatMoney(supplier.total_purchases)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-emerald-700">
                        {formatMoney(supplier.total_paid)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-red-600">
                        {formatMoney(supplier.total_due)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <button
                          type="button"
                          onClick={() => setDetailsSupplier(supplier)}
                          className="max-w-52 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-left transition hover:border-violet-300 hover:bg-violet-100"
                        >
                          <span className="block text-sm font-bold text-violet-700">
                            {productsBySupplier[String(supplier.id)]?.length ||
                              Number(supplier.products_count || 0)}{" "}
                            produkte
                          </span>
                          <span className="mt-1 block truncate text-xs text-violet-600">
                            {productsBySupplier[String(supplier.id)]?.length
                              ? productsBySupplier[String(supplier.id)]
                                  .slice(0, 2)
                                  .map((product) => product.name)
                                  .join(", ")
                              : "Shiko produktet"}
                          </span>
                        </button>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <PaymentStatusBadge status={supplier.payment_status} />
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDetailsSupplier(supplier)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                          >
                            Detaje
                          </button>

                          <button
                            type="button"
                            onClick={() => setPurchaseSupplier(supplier)}
                            className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition hover:bg-violet-100"
                          >
                            + Blerje
                          </button>

                          <button
                            type="button"
                            onClick={() => setPaymentSupplier(supplier)}
                            disabled={Number(supplier.total_due || 0) <= 0}
                            className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                              Number(supplier.total_due || 0) > 0
                                ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            } disabled:cursor-default`}
                          >
                            {Number(supplier.total_due || 0) > 0
                              ? "+ Pagesë"
                              : "✓ Shlyer"}
                          </button>

                          <button
                            type="button"
                            onClick={() => openEditModal(supplier)}
                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                          >
                            Modifiko
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}

          {!loading && filteredSuppliers.length > 0 && (
            <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                Duke shfaqur{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                </span>{" "}
                –{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(
                    currentPage * ITEMS_PER_PAGE,
                    filteredSuppliers.length,
                  )}
                </span>{" "}
                nga{" "}
                <span className="font-semibold text-slate-700">
                  {filteredSuppliers.length}
                </span>
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Para
                </button>

                <span className="flex min-w-20 items-center justify-center rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
                  {currentPage} / {totalPages}
                </span>

                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((page) =>
                      Math.min(totalPages, page + 1),
                    )
                  }
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Pas
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {editingSupplier
                    ? "Modifiko furnitorin"
                    : "Shto furnitor"}
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Plotëso informacionin e furnitorit.
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-600 transition hover:bg-slate-200"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
                {error && (
                  <div className="md:col-span-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {error}
                  </div>
                )}

                <FormField
                  label="Emri i furnitorit"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="P.sh. Neptun Albania"
                  required
                />

                <FormField
                  label="Personi i kontaktit"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder="Emër dhe mbiemër"
                />

                <FormField
                  label="Telefoni"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="069 123 4567"
                />

                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="furnitor@shembull.al"
                />

                <FormField
                  label="NIPT"
                  name="tax_number"
                  value={formData.tax_number}
                  onChange={handleInputChange}
                  placeholder="L12345678A"
                />

                <FormField
                  label="Adresa"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Tiranë, Shqipëri"
                />

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Shënime
                  </label>

                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Shënime për furnitorin..."
                    className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  />
                </div>

                <label className="md:col-span-2 flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />

                  <span>
                    <span className="block text-sm font-semibold text-slate-800">
                      Furnitor aktiv
                    </span>

                    <span className="block text-xs text-slate-500">
                      Furnitori mund të përdoret gjatë regjistrimit të
                      produkteve.
                    </span>
                  </span>
                </label>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                >
                  Anulo
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? "Duke ruajtur..."
                    : editingSupplier
                      ? "Ruaj ndryshimet"
                      : "Shto furnitorin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {purchaseSupplier && (
        <SupplierPurchaseModal
          supplier={purchaseSupplier}
          token={token}
          onClose={() => setPurchaseSupplier(null)}
          onSaved={(message) => {
            setPurchaseSupplier(null);
            setSuccessMessage(message || "Blerja u regjistrua me sukses.");
            fetchSuppliers();
          }}
        />
      )}

      {paymentSupplier && (
        <SupplierPaymentModal
          supplier={paymentSupplier}
          token={token}
          onClose={() => setPaymentSupplier(null)}
          onSaved={(message) => {
            setPaymentSupplier(null);
            setSuccessMessage(message || "Pagesa u regjistrua me sukses.");
            fetchSuppliers();
          }}
        />
      )}

      {productsSupplier && (
        <SupplierProductsModal
          supplier={productsSupplier}
          products={productsBySupplier[String(productsSupplier.id)] || []}
          onClose={() => setProductsSupplier(null)}
        />
      )}

    

      {detailsSupplier && (
        <SupplierDetailsModal
          supplier={detailsSupplier}
          token={token}
          onClose={() => setDetailsSupplier(null)}
          onPurchase={() => {
            setDetailsSupplier(null);
            setPurchaseSupplier(detailsSupplier);
          }}
          onPayment={() => {
            setDetailsSupplier(null);
            setPaymentSupplier(detailsSupplier);
          }}
        />
      )}
      </main>
    </div>
  );
}

function StatCard({ title, value, color, hint, active = false, onClick }) {
  const colors = {
    blue: "border-blue-100 bg-blue-50 text-blue-700",
    green: "border-emerald-100 bg-emerald-50 text-emerald-700",
    orange: "border-orange-100 bg-orange-50 text-orange-700",
    purple: "border-violet-100 bg-violet-50 text-violet-700",
    red: "border-red-100 bg-red-50 text-red-700",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full rounded-2xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-blue-100 ${
        active ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"
      }`}
    >
      <p className="text-sm font-medium text-slate-500">{title}</p>

      <div
        className={`mt-3 inline-flex min-w-16 justify-center rounded-xl border px-4 py-2 text-2xl font-bold ${
          colors[color] || colors.blue
        }`}
      >
        {value}
      </div>

      <p className="mt-3 text-xs font-semibold text-slate-400 transition group-hover:text-blue-600">
        {hint} <span aria-hidden="true">→</span>
      </p>
    </button>
  );
}

function MobileAmountCard({ label, value, color }) {
  const colors = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    purple: "border-violet-100 bg-violet-50 text-violet-700",
    green: "border-emerald-100 bg-emerald-50 text-emerald-700",
    red: "border-red-100 bg-red-50 text-red-600",
  };

  return (
    <div className={`min-w-0 rounded-xl border p-2.5 ${colors[color]}`}>
      <p className="truncate text-[10px] font-bold uppercase opacity-70">
        {label}
      </p>
      <p className="mt-1 truncate text-xs font-bold" title={formatMoney(value)}>
        {formatCompactMoney(value)}
      </p>
    </div>
  );
}

function TableHeader({ children, align = "left" }) {
  return (
    <th
      className={`whitespace-nowrap px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function FormField({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      />
    </div>
  );
}

function formatMoney(value) {
  return new Intl.NumberFormat("sq-AL", {
    style: "currency",
    currency: "ALL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatCompactMoney(value) {
  return `${new Intl.NumberFormat("sq-AL", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0))} ALL`;
}

function PaymentStatusBadge({ status }) {
  const statuses = {
    paid: {
      label: "Shlyer",
      classes: "border-emerald-200 bg-emerald-50 text-emerald-700",
    },
    partial: {
      label: "Pjesërisht",
      classes: "border-amber-200 bg-amber-50 text-amber-700",
    },
    unpaid: {
      label: "Pa shlyer",
      classes: "border-red-200 bg-red-50 text-red-700",
    },
    no_purchases: {
      label: "Pa blerje",
      classes: "border-slate-200 bg-slate-50 text-slate-600",
    },
  };

  const currentStatus = statuses[status] || statuses.no_purchases;

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${currentStatus.classes}`}
    >
      {currentStatus.label}
    </span>
  );
}

function PurchaseSupplierPicker({ suppliers, onClose, onSelect }) {
  const [search, setSearch] = useState("");
  const activeSuppliers = suppliers.filter((supplier) => {
    const matchesSearch = supplier.name
      ?.toLowerCase()
      .includes(search.trim().toLowerCase());
    return Number(supplier.is_active) === 1 && matchesSearch;
  });

  return (
    <ModalShell
      title="Zgjidh furnitorin"
      subtitle="Zgjidh furnitorin për të regjistruar blerjen e re."
      onClose={onClose}
      closeDisabled={false}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4 p-6">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Kërko furnitorin..."
          autoFocus
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
        />

        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {activeSuppliers.length === 0 ? (
            <p className="rounded-xl bg-slate-50 p-5 text-center text-sm text-slate-500">
              Nuk u gjet asnjë furnitor aktiv.
            </p>
          ) : (
            activeSuppliers.map((supplier) => (
              <button
                key={supplier.id}
                type="button"
                onClick={() => onSelect(supplier)}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 p-4 text-left transition hover:border-violet-300 hover:bg-violet-50"
              >
                <div>
                  <p className="font-bold text-slate-900">{supplier.name}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {supplier.phone || "Pa telefon"} · NIPT: {supplier.tax_number || "—"}
                  </p>
                </div>
                <span className="text-sm font-bold text-violet-700">
                  Zgjidh →
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function SupplierDetailsModal({
  supplier,
  token,
  onClose,
  onPurchase,
  onPayment,
}) {
  const [purchases, setPurchases] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [detailsError, setDetailsError] = useState("");
  const [detailsFilter, setDetailsFilter] = useState("all");

  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      try {
        setLoadingDetails(true);
        setDetailsError("");

        let response = await fetch(
          `${API_BASE_URL}/api/suppliers/${supplier.id}/details`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.status === 404) {
          response = await fetch(
            `${API_BASE_URL}/api/supplier-purchases?supplier_id=${supplier.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || "Detajet nuk mund të ngarkohen.");
        }

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.purchases)
            ? data.purchases
            : [];

        if (isMounted) {
          setPurchases(
            list.map((purchase) => ({
              ...purchase,
              items: normalizeArray(purchase.items),
              payments: normalizeArray(purchase.payments),
            })),
          );
        }
      } catch (requestError) {
        if (isMounted) setDetailsError(requestError.message);
      } finally {
        if (isMounted) setLoadingDetails(false);
      }
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [supplier.id, token]);

  const normalizedPurchases = purchases.map((purchase) => {
    const totalAmount = Number(purchase.total_amount || 0);
    const paymentsTotal = purchase.payments.reduce(
      (total, payment) => total + Number(payment.amount || 0),
      0,
    );
    const totalPaid = Number(purchase.total_paid ?? paymentsTotal);
    const totalDue = Number(
      purchase.total_due ?? Math.max(totalAmount - totalPaid, 0),
    );

    return { ...purchase, totalAmount, totalPaid, totalDue };
  });

  const filteredPurchases = normalizedPurchases.filter((purchase) => {
    if (detailsFilter === "paid") return purchase.totalDue <= 0;
    if (detailsFilter === "due") return purchase.totalDue > 0;
    return true;
  });

  const summary = normalizedPurchases.reduce(
    (totals, purchase) => ({
      purchases: totals.purchases + purchase.totalAmount,
      paid: totals.paid + purchase.totalPaid,
      due: totals.due + purchase.totalDue,
    }),
    { purchases: 0, paid: 0, due: 0 },
  );

  return (
    <ModalShell
      title={supplier.name}
      subtitle="Blerjet, produktet, pagesat dhe detyrimet e furnitorit"
      onClose={onClose}
      closeDisabled={false}
      maxWidth="max-w-6xl"
    >
      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <DetailSummaryCard
            label="Fatura / blerje"
            value={normalizedPurchases.length}
            color="blue"
          />
          <DetailSummaryCard
            label="Blerje totale"
            value={formatMoney(summary.purchases || supplier.total_purchases)}
            color="violet"
          />
          <DetailSummaryCard
            label="Shlyer"
            value={formatMoney(summary.paid || supplier.total_paid)}
            color="green"
          />
          <DetailSummaryCard
            label="Detyrim"
            value={formatMoney(summary.due || supplier.total_due)}
            color="red"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <SupplierInfo label="Kontakt" value={supplier.contact_person} />
          <SupplierInfo label="Telefon" value={supplier.phone} />
          <SupplierInfo label="Email" value={supplier.email} />
          <SupplierInfo label="NIPT" value={supplier.tax_number} />
          <div className="sm:col-span-2 lg:col-span-4">
            <SupplierInfo label="Adresë" value={supplier.address} />
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              ["all", "Të gjitha"],
              ["due", "Pa shlyer / pjesërisht"],
              ["paid", "Të shlyera"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setDetailsFilter(value)}
                className={`rounded-lg px-4 py-2 text-xs font-bold transition ${
                  detailsFilter === value
                    ? "bg-slate-900 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm font-semibold text-slate-500">
            {filteredPurchases.length} rezultate
          </p>
        </div>

        {detailsError && <ModalError message={detailsError} />}

        {loadingDetails ? (
          <p className="rounded-xl bg-slate-50 p-8 text-center text-sm text-slate-500">
            Duke ngarkuar historikun...
          </p>
        ) : filteredPurchases.length === 0 ? (
          <p className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
            Nuk ka blerje në këtë kategori.
          </p>
        ) : (
          <div className="space-y-4">
            {filteredPurchases.map((purchase) => (
              <PurchaseDetailsCard key={purchase.id} purchase={purchase} />
            ))}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Mbyll
        </button>

        <button
          type="button"
          onClick={onPayment}
          disabled={Number(supplier.total_due || 0) <= 0}
          className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          + Regjistro pagesë
        </button>
      </div>
    </ModalShell>
  );
}

function PurchaseDetailsCard({ purchase }) {
  const status =
    purchase.totalDue <= 0
      ? "paid"
      : purchase.totalPaid > 0
        ? "partial"
        : "unpaid";

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-slate-900">
              {purchase.invoice_number || `Blerja #${purchase.id}`}
            </h3>
            <PaymentStatusBadge status={status} />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Blerë më: {formatDate(purchase.purchase_date)}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4 text-right text-xs">
          <AmountInfo label="Totali" value={purchase.totalAmount} />
          <AmountInfo label="Shlyer" value={purchase.totalPaid} color="green" />
          <AmountInfo label="Mbetur" value={purchase.totalDue} color="red" />
        </div>
      </div>

      <div className="grid grid-cols-1 divide-y divide-slate-200 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <div className="p-4">
          <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
            Produktet e blera
          </h4>
          {purchase.items.length === 0 ? (
            <p className="text-sm text-slate-400">Nuk ka detaje produktesh.</p>
          ) : (
            <div className="space-y-2">
              {purchase.items.map((item, index) => (
                <div
                  key={item.id || `${item.product_id}-${index}`}
                  className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 p-3 text-sm"
                >
                  <div>
                    <p className="font-semibold text-slate-800">
                      {item.product_name || item.name || `Produkti #${item.product_id}`}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {Number(item.quantity || 0)} × {formatMoney(item.unit_purchase_price)}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-blue-600">
                      Marrë më: {formatDate(purchase.purchase_date)}
                    </p>
                  </div>
                  <p className="font-bold text-slate-800">
                    {formatMoney(
                      item.total_price ??
                        Number(item.quantity || 0) *
                          Number(item.unit_purchase_price || 0),
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4">
          <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
            Pagesat
          </h4>
          {purchase.payments.length === 0 ? (
            <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-600">
              Nuk është regjistruar asnjë pagesë.
            </p>
          ) : (
            <div className="space-y-2">
              {purchase.payments.map((payment, index) => (
                <div
                  key={payment.id || index}
                  className="flex items-start justify-between gap-4 rounded-lg bg-emerald-50 p-3 text-sm"
                >
                  <div>
                    <p className="font-semibold text-emerald-800">
                      Likuiduar më: {formatDate(payment.payment_date)}
                    </p>
                    <p className="mt-1 text-xs text-emerald-600">
                      {payment.payment_method || "Pa përcaktuar"}
                      {payment.reference_number
                        ? ` · ${payment.reference_number}`
                        : ""}
                    </p>
                  </div>
                  <p className="font-bold text-emerald-800">
                    {formatMoney(payment.amount)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function DetailSummaryCard({ label, value, color }) {
  const colors = {
    blue: "border-blue-100 bg-blue-50 text-blue-800",
    violet: "border-violet-100 bg-violet-50 text-violet-800",
    green: "border-emerald-100 bg-emerald-50 text-emerald-800",
    red: "border-red-100 bg-red-50 text-red-700",
  };

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-bold uppercase opacity-70">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}

function SupplierInfo({ label, value }) {
  return (
    <p>
      <span className="block text-xs font-bold uppercase text-slate-400">
        {label}
      </span>
      <span className="mt-1 block font-semibold text-slate-700">
        {value || "—"}
      </span>
    </p>
  );
}

function AmountInfo({ label, value, color }) {
  const colorClass =
    color === "green"
      ? "text-emerald-700"
      : color === "red"
        ? "text-red-600"
        : "text-slate-800";

  return (
    <div>
      <p className="text-slate-400">{label}</p>
      <p className={`mt-1 font-bold ${colorClass}`}>{formatMoney(value)}</p>
    </div>
  );
}

function normalizeArray(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("sq-AL").format(date);
}

function createPurchaseItem() {
  return {
    mode: "existing",
    product_id: "",
    product_search: "",
    product_dropdown_open: false,
    quantity: 1,
    unit_purchase_price: "",
    new_product: {
      name: "",
      price: "",
      category: "",
      description: "",
      barcode: "",
      imei: "",
      product_condition: "",
      brand: "",
      model: "",
      memory: "",
      ram: "",
      battery: "",
      color: "",
    },
  };
}

function SupplierPurchaseModal({ supplier, token, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [modalError, setModalError] = useState("");
  const [form, setForm] = useState({
    invoice_number: "",
    purchase_date: today,
    initial_payment: "0",
    payment_method: "cash",
    notes: "",
    items: [createPurchaseItem()],
  });

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await fetch(`${API_BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || "Produktet nuk mund të ngarkohen.");
        }

        const data = await response.json();
        const productList = Array.isArray(data)
          ? data
          : Array.isArray(data.products)
            ? data.products
            : [];

        if (isMounted) setProducts(productList);
      } catch (requestError) {
        if (isMounted) setModalError(requestError.message);
      } finally {
        if (isMounted) setLoadingProducts(false);
      }
    };

    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const total = form.items.reduce(
    (sum, item) =>
      sum + Number(item.quantity || 0) * Number(item.unit_purchase_price || 0),
    0,
  );

  const updateItem = (index, field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const updateNewProduct = (index, field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              new_product: { ...item.new_product, [field]: value },
            }
          : item,
      ),
    }));
  };

  const searchProduct = (index, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              mode: "existing",
              product_id: "",
              product_search: value,
              product_dropdown_open: true,
            }
          : { ...item, product_dropdown_open: false },
      ),
    }));
  };

  const selectProduct = (index, productId) => {
    const isNewProduct = productId === "__new__";
    const selectedProduct = products.find(
      (product) => String(product.id) === String(productId),
    );

    setForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              mode: isNewProduct ? "new" : "existing",
              product_id: isNewProduct ? "" : productId,
              product_search: isNewProduct ? "" : selectedProduct?.name || "",
              product_dropdown_open: false,
              unit_purchase_price:
                isNewProduct
                  ? ""
                  : selectedProduct?.purchase_price ?? item.unit_purchase_price,
            }
          : item,
      ),
    }));
  };

  const addItem = () => {
    setForm((currentForm) => ({
      ...currentForm,
      items: [
        ...currentForm.items,
        createPurchaseItem(),
      ],
    }));
  };

  const removeItem = (index) => {
    setForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const submitPurchase = async (event) => {
    event.preventDefault();
    setModalError("");

    const validItems = form.items.filter((item) => {
      const validBase =
        Number(item.quantity) > 0 && Number(item.unit_purchase_price) >= 0;

      if (item.mode === "new") {
        return (
          validBase &&
          item.new_product.name.trim() &&
          item.new_product.category.trim() &&
          Number(item.new_product.price) >= 0
        );
      }

      return validBase && item.product_id;
    });

    if (validItems.length !== form.items.length || validItems.length === 0) {
      setModalError(
        "Plotëso produktin, sasinë dhe çmimin. Për produktin e ri duhen emri, kategoria dhe çmimi i shitjes.",
      );
      return;
    }

    if (Number(form.initial_payment || 0) > total) {
      setModalError("Pagesa fillestare nuk mund të jetë më e madhe se blerja.");
      return;
    }

    try {
      setSavingPurchase(true);
      const response = await fetch(`${API_BASE_URL}/api/supplier-purchases`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          supplier_id: supplier.id,
          invoice_number: form.invoice_number.trim() || null,
          purchase_date: form.purchase_date,
          initial_payment: Number(form.initial_payment || 0),
          payment_method: form.payment_method,
          notes: form.notes.trim() || null,
          items: validItems.map((item) => ({
            product_id:
              item.mode === "existing" ? Number(item.product_id) : null,
            quantity: Number(item.quantity),
            unit_purchase_price: Number(item.unit_purchase_price),
            new_product:
              item.mode === "new"
                ? {
                    ...item.new_product,
                    name: item.new_product.name.trim(),
                    price: Number(item.new_product.price),
                    category: item.new_product.category.trim(),
                    description:
                      item.new_product.description.trim() ||
                      item.new_product.name.trim(),
                  }
                : null,
          })),
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Blerja nuk mund të ruhet.");
      }

      onSaved(result.message);
    } catch (requestError) {
      setModalError(requestError.message);
    } finally {
      setSavingPurchase(false);
    }
  };

  return (
    <ModalShell
      title="Regjistro blerje"
      subtitle={`Furnitori: ${supplier.name}`}
      onClose={onClose}
      closeDisabled={savingPurchase}
      maxWidth="max-w-5xl"
    >
      <form onSubmit={submitPurchase}>
        <div className="space-y-6 p-6">
          {modalError && <ModalError message={modalError} />}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Numri i faturës"
              name="invoice_number"
              value={form.invoice_number}
              onChange={(event) =>
                setForm({ ...form, invoice_number: event.target.value })
              }
              placeholder="P.sh. FAT-2026-001"
            />
            <FormField
              label="Data e blerjes"
              name="purchase_date"
              type="date"
              value={form.purchase_date}
              onChange={(event) =>
                setForm({ ...form, purchase_date: event.target.value })
              }
              required
            />
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-900">Produktet e blera</h3>
                <p className="text-xs text-slate-500">
                  Stoku dhe çmimi i blerjes përditësohen pas ruajtjes.
                </p>
              </div>
              <button
                type="button"
                onClick={addItem}
                className="rounded-lg bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700 hover:bg-violet-100"
              >
                + Shto produkt
              </button>
            </div>

            {loadingProducts ? (
              <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500">
                Duke ngarkuar produktet...
              </p>
            ) : (
              <div className="space-y-3">
                {form.items.map((item, index) => (
                  <div
                    key={index}
                    className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="flex items-end gap-3">
                      <div className="min-w-0 flex-1">
                        <label className="mb-2 block text-xs font-bold text-slate-600">
                          Kërko produktin ose shtoje manualisht
                        </label>
                        <div className="relative">
                          <input
                            type="search"
                            role="combobox"
                            aria-expanded={item.product_dropdown_open}
                            autoComplete="off"
                            value={item.product_search || ""}
                            onFocus={() =>
                              updateItem(index, "product_dropdown_open", true)
                            }
                            onChange={(event) =>
                              searchProduct(index, event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Escape") {
                                updateItem(index, "product_dropdown_open", false);
                              }
                            }}
                            placeholder="Zgjidh ose kërko produktin sipas emrit..."
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 pr-10 text-sm outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                          />
                          <button
                            type="button"
                            aria-label="Hap listën e produkteve"
                            onClick={() =>
                              updateItem(
                                index,
                                "product_dropdown_open",
                                !item.product_dropdown_open,
                              )
                            }
                            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-500"
                          >
                            <span
                              className={`transition ${
                                item.product_dropdown_open ? "rotate-180" : ""
                              }`}
                            >
                              ▾
                            </span>
                          </button>

                          {item.product_dropdown_open && (
                            <div className="absolute z-40 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                              <button
                                type="button"
                                onClick={() => selectProduct(index, "__new__")}
                                className="w-full rounded-lg px-3 py-3 text-left text-sm font-semibold text-blue-600 hover:bg-blue-50"
                              >
                                + Produkti nuk ekziston — shtoje manualisht
                              </button>

                              {products.filter((product) =>
                                String(product.name || "")
                                  .toLocaleLowerCase("sq-AL")
                                  .includes(
                                    String(item.product_search || "")
                                      .trim()
                                      .toLocaleLowerCase("sq-AL"),
                                  ),
                              ).length === 0 ? (
                                <p className="px-3 py-4 text-sm text-slate-500">
                                  Nuk u gjet asnjë produkt me këtë emër.
                                </p>
                              ) : (
                                products
                                  .filter((product) =>
                                    String(product.name || "")
                                      .toLocaleLowerCase("sq-AL")
                                      .includes(
                                        String(item.product_search || "")
                                          .trim()
                                          .toLocaleLowerCase("sq-AL"),
                                      ),
                                  )
                                  .map((product) => (
                                    <button
                                      type="button"
                                      key={product.id}
                                      onClick={() =>
                                        selectProduct(index, product.id)
                                      }
                                      className={`w-full rounded-lg px-3 py-3 text-left text-sm hover:bg-violet-50 ${
                                        String(item.product_id) ===
                                        String(product.id)
                                          ? "bg-violet-50 font-bold text-violet-700"
                                          : "text-slate-700"
                                      }`}
                                    >
                                      {product.name}
                                    </button>
                                  ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        disabled={form.items.length === 1}
                        title="Hiq produktin"
                        className="h-11 w-11 shrink-0 rounded-xl border border-red-200 bg-white text-lg text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        ×
                      </button>
                    </div>

                    {item.mode === "new" && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                        <div className="mb-4">
                          <h4 className="font-bold text-blue-900">
                            Regjistro produkt të ri
                          </h4>
                          <p className="mt-1 text-xs text-blue-700">
                            Produkti do të ruhet automatikisht në tabelën Products.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <FormField
                            label="Emri i produktit"
                            name={`new-name-${index}`}
                            value={item.new_product.name}
                            onChange={(event) =>
                              updateNewProduct(index, "name", event.target.value)
                            }
                            placeholder="P.sh. iPhone 15 Pro"
                            required
                          />
                          <FormField
                            label="Kategoria"
                            name={`new-category-${index}`}
                            value={item.new_product.category}
                            onChange={(event) =>
                              updateNewProduct(index, "category", event.target.value)
                            }
                            placeholder="P.sh. Telefona"
                            required
                          />
                          <FormField
                            label="Çmimi i shitjes"
                            name={`new-sale-price-${index}`}
                            type="number"
                            value={item.new_product.price}
                            onChange={(event) =>
                              updateNewProduct(index, "price", event.target.value)
                            }
                            placeholder="0"
                            required
                          />
                          <FormField
                            label="IMEI"
                            name={`new-imei-${index}`}
                            value={item.new_product.imei}
                            onChange={(event) =>
                              updateNewProduct(index, "imei", event.target.value)
                            }
                            placeholder="Opsionale"
                          />
                          <FormField
                            label="Barkodi"
                            name={`new-barcode-${index}`}
                            value={item.new_product.barcode}
                            onChange={(event) =>
                              updateNewProduct(index, "barcode", event.target.value)
                            }
                            placeholder="Opsionale"
                          />
                          <FormField
                            label="Gjendja"
                            name={`new-condition-${index}`}
                            value={item.new_product.product_condition}
                            onChange={(event) =>
                              updateNewProduct(
                                index,
                                "product_condition",
                                event.target.value,
                              )
                            }
                            placeholder="I ri / I përdorur"
                          />
                          <FormField
                            label="Marka"
                            name={`new-brand-${index}`}
                            value={item.new_product.brand}
                            onChange={(event) =>
                              updateNewProduct(index, "brand", event.target.value)
                            }
                            placeholder="P.sh. Apple"
                          />
                          <FormField
                            label="Modeli"
                            name={`new-model-${index}`}
                            value={item.new_product.model}
                            onChange={(event) =>
                              updateNewProduct(index, "model", event.target.value)
                            }
                            placeholder="P.sh. A3101"
                          />
                          <FormField
                            label="Ngjyra"
                            name={`new-color-${index}`}
                            value={item.new_product.color}
                            onChange={(event) =>
                              updateNewProduct(index, "color", event.target.value)
                            }
                            placeholder="Opsionale"
                          />
                          <div className="md:col-span-3">
                            <label className="mb-2 block text-sm font-semibold text-slate-700">
                              Përshkrimi
                            </label>
                            <textarea
                              rows="2"
                              value={item.new_product.description}
                              onChange={(event) =>
                                updateNewProduct(
                                  index,
                                  "description",
                                  event.target.value,
                                )
                              }
                              placeholder="Nëse lihet bosh, përdoret emri i produktit."
                              className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <FormField
                        label="Sasia"
                        name={`quantity-${index}`}
                        type="number"
                        value={item.quantity}
                        onChange={(event) =>
                          updateItem(index, "quantity", event.target.value)
                        }
                        required
                      />
                      <FormField
                        label="Çmimi i blerjes për njësi"
                        name={`price-${index}`}
                        type="number"
                        value={item.unit_purchase_price}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "unit_purchase_price",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField
              label="Pagesa fillestare"
              name="initial_payment"
              type="number"
              value={form.initial_payment}
              onChange={(event) =>
                setForm({ ...form, initial_payment: event.target.value })
              }
            />
            <SelectField
              label="Mënyra e pagesës"
              value={form.payment_method}
              onChange={(event) =>
                setForm({ ...form, payment_method: event.target.value })
              }
              options={PAYMENT_METHODS}
            />
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-xs font-bold uppercase text-blue-600">
                Totali i blerjes
              </p>
              <p className="mt-1 text-2xl font-bold text-blue-800">
                {formatMoney(total)}
              </p>
              <p className="mt-1 text-xs text-blue-600">
                Mbeten {formatMoney(Math.max(total - Number(form.initial_payment || 0), 0))}
              </p>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Shënime
            </label>
            <textarea
              rows="3"
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              placeholder="Shënime për këtë blerje..."
            />
          </div>
        </div>

        <ModalActions
          onClose={onClose}
          saving={savingPurchase}
          disabled={loadingProducts}
          submitLabel="Regjistro blerjen"
        />
      </form>
    </ModalShell>
  );
}

function SupplierPaymentModal({ supplier, token, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [purchases, setPurchases] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [savingPayment, setSavingPayment] = useState(false);
  const [modalError, setModalError] = useState("");
  const [form, setForm] = useState({
    amount: "",
    payment_date: today,
    payment_method: "cash",
    reference_number: "",
    notes: "",
  });

  useEffect(() => {
    let isMounted = true;

    const fetchPurchases = async () => {
      try {
        setLoadingPayments(true);
        const response = await fetch(
          `${API_BASE_URL}/api/suppliers/${supplier.id}/details`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || "Detyrimi nuk mund të ngarkohet.");
        }

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.purchases)
            ? data.purchases
            : [];
        const unpaidPurchases = list
          .filter((purchase) => Number(purchase.total_due || 0) > 0)
          .sort(
            (first, second) =>
              new Date(first.purchase_date || 0) -
                new Date(second.purchase_date || 0) ||
              Number(first.id) - Number(second.id),
          );

        if (isMounted) {
          setPurchases(unpaidPurchases);
        }
      } catch (requestError) {
        if (isMounted) setModalError(requestError.message);
      } finally {
        if (isMounted) setLoadingPayments(false);
      }
    };

    fetchPurchases();
    return () => {
      isMounted = false;
    };
  }, [supplier.id, token]);

  const totalDue = purchases.reduce(
    (total, purchase) => total + Number(purchase.total_due || 0),
    0,
  );

  const submitPayment = async (event) => {
    event.preventDefault();
    setModalError("");
    const amount = Number(form.amount);

    if (amount <= 0 || amount > totalDue) {
      setModalError(
        `Pagesa duhet të jetë më e madhe se zero dhe jo më shumë se ${formatMoney(totalDue)}.`,
      );
      return;
    }

    try {
      setSavingPayment(true);
      let remainingAmount = amount;

      for (const purchase of purchases) {
        if (remainingAmount <= 0) break;

        const purchaseDue = Number(purchase.total_due || 0);
        const paymentForPurchase = Math.min(remainingAmount, purchaseDue);

        const response = await fetch(
          `${API_BASE_URL}/api/supplier-purchases/${purchase.id}/payments`,
          {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: paymentForPurchase,
            payment_date: form.payment_date,
            payment_method: form.payment_method,
            reference_number: form.reference_number.trim() || null,
            notes:
              form.notes.trim() ||
              `Pagesë nga shlyerja totale për ${supplier.name}`,
          }),
          },
        );

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.message || "Pagesa nuk mund të ruhet.");
        }

        remainingAmount = Number(
          (remainingAmount - paymentForPurchase).toFixed(2),
        );
      }

      onSaved("Pagesa totale u regjistrua me sukses.");
    } catch (requestError) {
      setModalError(requestError.message);
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <ModalShell
      title="Regjistro pagesë"
      subtitle={`Furnitori: ${supplier.name}`}
      onClose={onClose}
      closeDisabled={savingPayment}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={submitPayment}>
        <div className="space-y-5 p-6">
          {modalError && <ModalError message={modalError} />}

          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-xs font-bold uppercase text-red-600">
              Detyrimi total ndaj furnitorit
            </p>
            <p className="mt-1 text-2xl font-bold text-red-700">
              {formatMoney(totalDue || supplier.total_due)}
            </p>
          </div>

          {loadingPayments ? (
            <p className="text-sm text-slate-500">Duke ngarkuar detyrimin...</p>
          ) : purchases.length === 0 ? (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700">
              Ky furnitor nuk ka detyrim për shlyerje.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FormField
                    label="Pagesa totale për shlyerje"
                    name="amount"
                    type="number"
                    value={form.amount}
                    onChange={(event) =>
                      setForm({ ...form, amount: event.target.value })
                    }
                    placeholder="0"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, amount: String(totalDue) })}
                    className="mt-2 text-xs font-bold text-blue-600 hover:text-blue-700"
                  >
                    Shlyej të gjithë detyrimin: {formatMoney(totalDue)}
                  </button>
                </div>
                <FormField
                  label="Data e pagesës"
                  name="payment_date"
                  type="date"
                  value={form.payment_date}
                  onChange={(event) =>
                    setForm({ ...form, payment_date: event.target.value })
                  }
                  required
                />
                <SelectField
                  label="Mënyra e pagesës"
                  value={form.payment_method}
                  onChange={(event) =>
                    setForm({ ...form, payment_method: event.target.value })
                  }
                  options={PAYMENT_METHODS}
                />
                <FormField
                  label="Numri i referencës"
                  name="reference_number"
                  value={form.reference_number}
                  onChange={(event) =>
                    setForm({ ...form, reference_number: event.target.value })
                  }
                  placeholder="Referenca bankare (opsionale)"
                />
              </div>

              {Number(form.amount || 0) > 0 && (
                <div
                  className={`rounded-xl border p-4 ${
                    Math.max(totalDue - Number(form.amount || 0), 0) === 0
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  <p className="text-xs font-bold uppercase">
                    Pas regjistrimit të pagesës
                  </p>
                  <p className="mt-1 text-lg font-bold">
                    Mbeten:{" "}
                    {formatMoney(
                      Math.max(totalDue - Number(form.amount || 0), 0),
                    )}
                  </p>
                  <p className="mt-1 text-xs font-semibold">
                    {Math.max(totalDue - Number(form.amount || 0), 0) === 0
                      ? "Detyrimi ndaj furnitorit do të shlyhet plotësisht."
                      : "Do të mbetet ende një pjesë e detyrimit për t’u shlyer."}
                  </p>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Shënime
                </label>
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(event) =>
                    setForm({ ...form, notes: event.target.value })
                  }
                  className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Shënime për pagesën..."
                />
              </div>
            </>
          )}
        </div>

        <ModalActions
          onClose={onClose}
          saving={savingPayment}
          disabled={loadingPayments || purchases.length === 0}
          submitLabel="Regjistro pagesën"
        />
      </form>
    </ModalShell>
  );
}

function SupplierProductsModal({ supplier, products, onClose }) {
  const totalStockValue = products.reduce(
    (total, product) =>
      total +
      Number(product.purchase_price || 0) * Number(product.stock || 0),
    0,
  );

  return (
    <ModalShell
      title="Produktet e furnitorit"
      subtitle={supplier.name}
      onClose={onClose}
      closeDisabled={false}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-5 p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-xs font-bold uppercase text-blue-600">
              Produkte të lidhura
            </p>
            <p className="mt-1 text-2xl font-bold text-blue-800">
              {products.length}
            </p>
          </div>
          <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
            <p className="text-xs font-bold uppercase text-violet-600">
              Njësi në stok
            </p>
            <p className="mt-1 text-2xl font-bold text-violet-800">
              {products.reduce(
                (total, product) => total + Number(product.stock || 0),
                0,
              )}
            </p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-xs font-bold uppercase text-emerald-600">
              Vlera e stokut
            </p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">
              {formatMoney(totalStockValue)}
            </p>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-700">
            Nuk ka ende produkte të lidhura me këtë furnitor. Kur regjistron
            blerjen, produkti duhet të ruhet me
            <strong> supplier_id: {supplier.id}</strong>.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <TableHeader>Produkti</TableHeader>
                    <TableHeader>IMEI / Barkod</TableHeader>
                    <TableHeader>Çmimi i blerjes</TableHeader>
                    <TableHeader>Stoku</TableHeader>
                    <TableHeader>Totali</TableHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">
                          {product.name}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {[product.brand, product.model]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-600">
                        {product.imei || product.barcode || "—"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-slate-800">
                        {formatMoney(product.purchase_price)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-violet-700">
                        {Number(product.stock || 0)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-emerald-700">
                        {formatMoney(
                          Number(product.purchase_price || 0) *
                            Number(product.stock || 0),
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          Mbyll
        </button>
        <button
          type="button"
          onClick={() => {
            window.location.href = `/admin/products?supplier_id=${supplier.id}`;
          }}
          className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Shko te Produktet
        </button>
      </div>
    </ModalShell>
  );
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank", label: "Bankë" },
  { value: "card", label: "Kartë" },
  { value: "other", label: "Tjetër" },
];

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ModalShell({
  title,
  subtitle,
  onClose,
  closeDisabled,
  maxWidth,
  children,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !closeDisabled) onClose();
      }}
    >
      <div
        className={`max-h-[94vh] w-full overflow-y-auto rounded-2xl bg-white shadow-2xl ${maxWidth}`}
      >
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-600 hover:bg-slate-200 disabled:opacity-50"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onClose, saving, disabled, submitLabel }) {
  return (
    <div className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
      >
        Anulo
      </button>
      <button
        type="submit"
        disabled={saving || disabled}
        className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? "Duke ruajtur..." : submitLabel}
      </button>
    </div>
  );
}

function ModalError({ message }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
      {message}
    </div>
  );
}